import { FiShield } from 'react-icons/fi';

export const hedgingLanguageData = {
  courseId: 'hedging-language',
  courseType: 'vocabulary',
  title: 'Hedging Language',
  icon: FiShield,
  overview: {
    impact: 'Express uncertainty and avoid overgeneralization in academic writing',
    focus: 'Modal verbs, adverbs, and phrases that soften absolute claims',
    uses: 'Writing Task 2, Speaking Part 3, Reading comprehension',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'What Is Hedging?',
      content: `<p>Hedging is the use of cautious language to avoid making absolute or overly definitive claims. In academic writing, hedging demonstrates intellectual maturity — it shows you understand that most issues are complex and that few claims are universally true.</p>
<p>IELTS examiners reward hedging because it signals a sophisticated grasp of argumentation. Statements like <em>"Everyone agrees that…"</em> or <em>"Technology always causes…"</em> are red flags; hedged equivalents like <em>"It is widely accepted that…"</em> or <em>"Technology tends to cause…"</em> sound far more credible.</p>`,
      points: [
        'Hedging avoids absolutism — few claims in the real world are 100% true',
        'Examiners look for cautious, balanced language in Band 7+ writing',
        'Hedging is not weakness — it shows nuanced, critical thinking',
        'Over-hedging (too many hedges in one sentence) sounds unnatural and should be avoided',
      ],
    },
    {
      title: 'Hedging by Certainty Level',
      content: `<p>Hedging expressions exist on a spectrum from near-certainty to strong doubt. Choosing the right level of certainty is key to sounding natural and accurate.</p>
<table>
  <thead><tr><th>Certainty</th><th>Expressions</th><th>Example</th></tr></thead>
  <tbody>
    <tr><td><strong>~90%</strong></td><td>clearly, evidently, undoubtedly, it is well established that</td><td>It is well established that exercise improves mental health.</td></tr>
    <tr><td><strong>~75%</strong></td><td>generally, typically, tends to, in most cases, it is widely accepted that</td><td>Urbanisation generally leads to improved access to services.</td></tr>
    <tr><td><strong>~60%</strong></td><td>often, frequently, it appears that, evidence suggests</td><td>Evidence suggests that early intervention is more effective.</td></tr>
    <tr><td><strong>~45%</strong></td><td>may, might, could, it is possible that, arguably</td><td>This approach could reduce inequality to some extent.</td></tr>
    <tr><td><strong>~30%</strong></td><td>conceivably, it is not impossible that, there is limited evidence</td><td>There is limited evidence to support this claim.</td></tr>
  </tbody>
</table>`,
      points: [
        'Match your hedge strength to how confident the evidence allows you to be',
        '~75% hedges (generally, tends to) are the most versatile for IELTS essays',
        'Avoid stacking hedges: "It might possibly perhaps be…" sounds awkward',
        'Strong hedges (~90%) are appropriate when citing well-known facts',
      ],
    },
    {
      title: 'Hedging Verbs, Adverbs, and Phrases',
      content: `<p>Hedging tools fall into three main categories. Mastering all three gives you maximum flexibility.</p>
<h4>Hedging Verbs</h4>
<p><em>suggest, indicate, imply, appear, seem, tend, argue, claim, propose, assume</em></p>
<h4>Hedging Adverbs</h4>
<p><em>arguably, possibly, presumably, apparently, potentially, somewhat, partly, largely, generally, typically</em></p>
<h4>Hedging Noun Phrases &amp; Clauses</h4>
<p><em>the evidence suggests that, there is a tendency for, it could be argued that, one possible explanation is, to a certain extent, in some cases</em></p>`,
      points: [
        'Hedging verbs (suggest, indicate) replace "prove" or "show" for softer claims',
        'Adverbs (arguably, possibly) are the quickest way to hedge any statement',
        'Noun phrases (the evidence suggests) work well as sentence openers in Task 2',
        'Combine categories naturally: "Research generally suggests that…"',
      ],
    },
    {
      title: 'Using Hedging in IELTS Writing',
      content: `<p>In IELTS Writing Task 2, hedging belongs primarily in your body paragraphs — where you present arguments, evidence, and analysis. Your thesis statement can be direct, but your reasoning should be measured.</p>
<h4>Before (too absolute)</h4>
<p><em>"Social media destroys young people's mental health."</em></p>
<h4>After (appropriately hedged)</h4>
<p><em>"There is growing evidence that excessive social media use may have a detrimental impact on the mental well-being of young people."</em></p>`,
      points: [
        'Hedge claims in body paragraphs, not in your thesis (which should be clear)',
        'Use one or two hedges per claim — not more',
        'Pair hedging with evidence phrases: "Research suggests…", "Studies indicate…"',
        'In Speaking Part 3, hedging sounds sophisticated: "I\'d say that… tends to…"',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Hedging Expressions',
      type: 'select',
      instructions: 'Which word or phrase in each sentence is the hedging expression?',
      questions: [
        {
          text: '"Technology tends to improve productivity in most industries."',
          options: ['Technology', 'tends to', 'improve', 'most industries'],
          answer: 'tends to',
          explanation: '"Tends to" is a hedging verb that softens the claim, avoiding the absolute "Technology improves…"',
        },
        {
          text: '"It could be argued that higher taxes discourage entrepreneurship."',
          options: ['It could be argued that', 'higher taxes', 'discourage', 'entrepreneurship'],
          answer: 'It could be argued that',
          explanation: '"It could be argued that" is a hedging clause that presents the claim as one possible viewpoint rather than a fact.',
        },
        {
          text: '"Urbanisation has arguably been the most significant demographic shift of the century."',
          options: ['Urbanisation', 'arguably', 'most significant', 'demographic shift'],
          answer: 'arguably',
          explanation: '"Arguably" is a hedging adverb that qualifies the superlative claim, acknowledging other viewpoints exist.',
        },
        {
          text: '"There is some evidence that bilingual education may enhance cognitive flexibility."',
          options: ['There is some evidence that', 'bilingual education', 'enhance', 'cognitive flexibility'],
          answer: 'There is some evidence that',
          explanation: '"There is some evidence that" hedges by attributing the claim to evidence rather than stating it as established fact.',
        },
      ],
    },
    {
      title: 'Add Hedging to Absolute Statements',
      type: 'fill-in',
      instructions: 'Rewrite each absolute statement by adding an appropriate hedging word or phrase in the blank.',
      questions: [
        {
          text: 'Original: "Working from home increases productivity." → "Working from home _______ increases productivity."',
          answer: 'generally',
          explanation: '"Generally" softens the claim to acknowledge exceptions while still asserting the main tendency.',
        },
        {
          text: 'Original: "Fossil fuels cause climate change." → "_______, fossil fuels are a major contributor to climate change."',
          answer: 'It is widely accepted that',
          explanation: '"It is widely accepted that" attributes the claim to broad consensus, which is appropriate for well-established scientific views.',
        },
        {
          text: 'Original: "Children learn languages faster than adults." → "Children _______ learn languages faster than adults."',
          answer: 'tend to',
          explanation: '"Tend to" acknowledges the general pattern without claiming it applies in every single case.',
        },
        {
          text: 'Original: "This policy will solve the housing crisis." → "This policy _______ alleviate some aspects of the housing crisis."',
          answer: 'could potentially',
          explanation: '"Could potentially" introduces possibility rather than certainty, which is more realistic for policy predictions.',
        },
      ],
    },
    {
      title: 'Choose the Appropriate Certainty Level',
      type: 'select',
      instructions: 'Select the most appropriate hedging expression for each context.',
      questions: [
        {
          text: 'Claim based on strong scientific consensus: "_______ regular exercise reduces the risk of cardiovascular disease."',
          options: [
            'It is well established that',
            'It is conceivable that',
            'There is limited evidence that',
            'One might speculate that',
          ],
          answer: 'It is well established that',
          explanation: 'Strong scientific consensus calls for a high-certainty hedge (~90%). "Well established" matches this evidence level.',
        },
        {
          text: 'Speculative claim about the future: "Artificial intelligence _______ transform the job market within the next decade."',
          options: ['will definitely', 'could potentially', 'has already', 'undoubtedly will'],
          answer: 'could potentially',
          explanation: 'Future predictions require moderate hedging (~45%). "Could potentially" expresses possibility without overclaiming.',
        },
        {
          text: 'Observation with notable exceptions: "University graduates _______ earn higher salaries than non-graduates."',
          options: ['always', 'generally', 'conceivably', 'never fail to'],
          answer: 'generally',
          explanation: '"Generally" (~75%) fits an observation that is mostly but not always true, acknowledging exceptions.',
        },
        {
          text: 'Controversial claim with mixed evidence: "_______ social media has a negative impact on adolescent self-esteem."',
          options: [
            'It is undeniable that',
            'There is growing evidence that',
            'Everyone knows that',
            'It is certain that',
          ],
          answer: 'There is growing evidence that',
          explanation: 'Mixed evidence calls for ~60% certainty. "Growing evidence" signals an emerging but not settled consensus.',
        },
      ],
    },
    {
      title: 'Rewrite with Hedging',
      type: 'writing',
      instructions: 'Rewrite each absolute statement using appropriate hedging language. Aim for a natural academic tone.',
      questions: [
        {
          text: 'Rewrite: "Tourism always damages local cultures and traditions."',
          sampleAnswer: 'Tourism can, in some cases, have a detrimental effect on local cultures and traditions, although this is not universally the case.',
          explanation: 'The rewrite replaces "always" with hedges ("can", "in some cases") and adds a balancing clause.',
        },
        {
          text: 'Rewrite: "Online learning is better than traditional classroom education."',
          sampleAnswer: 'There is some evidence to suggest that online learning may offer certain advantages over traditional classroom education, particularly in terms of flexibility.',
          explanation: 'The rewrite avoids the absolute comparative by using "some evidence", "may", and "certain advantages".',
        },
        {
          text: 'Rewrite: "Governments must ban all fast food advertising to prevent obesity."',
          sampleAnswer: 'It could be argued that restricting fast food advertising might contribute to efforts to reduce obesity rates, though a complete ban may not be the most effective approach.',
          explanation: 'The rewrite replaces the absolute "must ban all" with measured language and acknowledges complexity.',
        },
      ],
    },
  ],
  tips: [
    'Start by mastering 5-6 versatile hedges: tends to, generally, it appears that, may, could, evidence suggests',
    'Read published academic articles and underline every hedge — you will find dozens per page',
    'In IELTS Writing, aim for 2-3 hedging expressions per body paragraph',
    'Avoid double hedging: "It might possibly perhaps…" weakens your writing',
    'Hedging in Speaking Part 3 sounds natural: "I would say that… generally… tends to…"',
  ],
  dailyPractice: [
    'Take one absolute statement from a news headline and rewrite it with hedging',
    'Read an IELTS model essay and identify all hedging expressions',
    'Write three sentences about a current topic using different certainty levels',
    'Practice Speaking Part 3 answers using hedging phrases aloud',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Hedging Language for IELTS: Academic hedging expressions (may, might, tends to, appears to, it is possible that, arguably), degrees of certainty from ~90% to ~30%, hedging verbs/adverbs/noun phrases, and applying hedging in Task 2 body paragraphs. Difficulty 4 — advanced academic writing skill.',
};

export function getDataForDifficulty(difficulty = 4) {
  if (difficulty <= 2) {
    return {
      ...hedgingLanguageData,
      title: hedgingLanguageData.title + ' (Foundations)',
      overview: { ...hedgingLanguageData.overview, duration: '15-20 minutes' },
      sections: hedgingLanguageData.sections.slice(0, Math.max(2, Math.ceil(hedgingLanguageData.sections.length / 2))),
      exercises: hedgingLanguageData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...hedgingLanguageData,
      title: hedgingLanguageData.title + ' (Advanced)',
      overview: { ...hedgingLanguageData.overview, duration: '30-35 minutes' },
      sections: hedgingLanguageData.sections,
      exercises: hedgingLanguageData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(hedgingLanguageData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return hedgingLanguageData;
}
